"use client"

import { Sparkles, X } from "lucide-react"

import { useT } from "@/lib/i18n"
import { useServiceWorkerUpdate } from "@/lib/use-service-worker-update"

/**
 * Sticky-top banner shown when a newer service worker is waiting. Mounted in
 * app/layout.tsx INSIDE the phone shell and BEFORE <PageTransition>, so it
 * stays above every per-page sticky header in document order despite its
 * lower z-index.
 *
 * Reload posts SKIP_WAITING to the waiting SW; the controllerchange listener
 * in components/service-worker.tsx then reloads the page. Later dismisses
 * the banner for the session only — it returns on the next tab open.
 */
export function PwaUpdateBanner() {
  const { updateReady, applyUpdate, dismiss } = useServiceWorkerUpdate()
  const t = useT()

  if (!updateReady) return null

  return (
    <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-surface-container-high bg-surface px-4 py-3 shadow-ambient">
      <div className="flex size-11 shrink-0 items-center justify-center rounded-[12px] bg-primary/10">
        <Sparkles className="size-5 text-primary" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">
          {t("pwa.update.title")}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {t("pwa.update.body")}
        </p>
      </div>
      <button
        type="button"
        onClick={applyUpdate}
        className="shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-maroon-container"
      >
        {t("pwa.update.reload")}
      </button>
      <button
        type="button"
        aria-label={t("pwa.update.later")}
        onClick={dismiss}
        className="shrink-0 text-muted-foreground"
      >
        <X className="size-5" />
      </button>
    </div>
  )
}
